import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as authAPI from './authAPI';

// Load auth state from localStorage on init
const loadAuthFromStorage = () => {
  try {
    const token = localStorage.getItem('authToken');
    const userJson = localStorage.getItem('authUser');
    if (token && userJson) {
      return {
        token,
        user: JSON.parse(userJson),
        status: 'succeeded'
      };
    }
  } catch (error) {
    console.error('Failed to load auth from storage:', error);
  }
  return {
    user: null,
    token: null,
    status: 'idle'
  };
};

const initialState = {
  ...loadAuthFromStorage(),
  forgotPasswordStatus: 'idle',
  forgotPasswordError: null,
  resetPasswordStatus: 'idle',
  resetPasswordError: null
};

// Async thunk for login
export const loginUser = createAsyncThunk(
  'auth/login',
  async ({ email, password }, { rejectWithValue }) => {
    try {
      const result = await authAPI.login(email, password);
      return result; // { user, token }
    } catch (error) {
      return rejectWithValue(error.message || 'Login failed');
    }
  }
);

// Forgot Password
export const forgotPassword = createAsyncThunk(
  'auth/forgotPassword',
  async (email, { rejectWithValue }) => {
    try {
      const result = await authAPI.forgotPassword(email);
      return result;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to send reset link');
    }
  }
);

// Reset Password
export const resetPassword = createAsyncThunk(
  'auth/resetPassword',
  async ({ token, newPassword }, { rejectWithValue }) => {
    try {
      const result = await authAPI.resetPassword({ token, newPassword });
      return result;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to reset password');
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    loginStart(state) { 
      state.status = 'loading'; 
      state.error = null;
    },
    loginSuccess(state, action) { 
      state.status = 'succeeded'; 
      state.user = action.payload.user; 
      state.token = action.payload.token;
      state.error = null;
      // Persist to localStorage
      localStorage.setItem('authToken', action.payload.token);
      localStorage.setItem('authUser', JSON.stringify(action.payload.user));
    },
    loginFailed(state, action) { 
      state.status = 'failed'; 
      state.error = action.payload || 'Login failed';
      state.user = null;
      state.token = null;
    },
    setCredentials(state, action) {
      state.status = 'succeeded';
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.error = null;
      // Persist to localStorage
      localStorage.setItem('authToken', action.payload.token);
      localStorage.setItem('authUser', JSON.stringify(action.payload.user));
    },
    logout(state) { 
      // Reset auth state
      state.user = null; 
      state.token = null; 
      state.status = 'idle';
      state.error = null;
      
      // Clear all localStorage items
      localStorage.clear();
      
      // Note: To reset other slices, we need to dispatch a special action
      // that the root reducer will handle. This is done in the Navbar component.
    },
    restoreAuth(state) {
      // Manual restore if needed (though we load on init)
      const restored = loadAuthFromStorage();
      state.user = restored.user;
      state.token = restored.token;
      state.status = restored.status;
    }
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(loginUser.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.error = null;
        localStorage.setItem('authToken', action.payload.token);
        localStorage.setItem('authUser', JSON.stringify(action.payload.user));
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Login failed';
        state.user = null;
        state.token = null;
      })
      // Forgot Password
      .addCase(forgotPassword.pending, (state) => {
        state.forgotPasswordStatus = 'loading';
        state.forgotPasswordError = null;
      })
      .addCase(forgotPassword.fulfilled, (state) => {
        state.forgotPasswordStatus = 'succeeded';
        state.forgotPasswordError = null;
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.forgotPasswordStatus = 'failed';
        state.forgotPasswordError = action.payload || 'Failed to send reset link';
      })
      // Reset Password
      .addCase(resetPassword.pending, (state) => {
        state.resetPasswordStatus = 'loading';
        state.resetPasswordError = null;
      })
      .addCase(resetPassword.fulfilled, (state) => {
        state.resetPasswordStatus = 'succeeded';
        state.resetPasswordError = null;
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.resetPasswordStatus = 'failed';
        state.resetPasswordError = action.payload || 'Failed to reset password';
      });
  }
});

export const { loginStart, loginSuccess, loginFailed, logout, restoreAuth, setCredentials } = authSlice.actions;
export default authSlice.reducer;
